import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './features/auth/AuthContext';
import ProtectedRoute from './features/auth/ProtectedRoute';
import Login from './features/auth/LoginPage';
import Dashboard from './features/dashboard/DashboardPage';
import Users from './features/users/UsersPage';
import UserActivities from './features/users/UserActivitiesPage';
import P2P from './features/p2p/P2PPage';
import Disputes from './features/p2p/DisputesPage';
import DisputeDetail from './features/p2p/DisputeDetailPage';
import PaymentMethods from './features/p2p/PaymentMethodsPage';
import Rates from './features/p2p/RatesPage';
import P2pRestrictions from './features/p2p/RestrictionsPage';
import OtcSettings from './features/otc/OtcSettingsPage';
import { OTC_ENABLED } from './shared/constants/features';
import Swaps from './features/swap/SwapsPage';
import SwapRates from './features/swap/RatesPage';
import Withdrawals from './features/withdrawal/WithdrawalsPage';
import WithdrawalRates from './features/withdrawal/RatesPage';
import HotWallets from './features/withdrawal/HotWalletsPage';
import Wallets from './features/ledger/WalletsPage';
import Balances from './features/ledger/BalancesPage';
import Deposits from './features/ledger/DepositsPage';
import FinanceOperations from './features/finance/OperationsPage';
import FinancePlatformRate from './features/finance/PlatformRatePage';
import FinanceP2pOperations from './features/finance/P2pOperationsPage';
import FinanceSwapOperations from './features/finance/SwapOperationsPage';
import FinanceWithdrawalOperations from './features/finance/WithdrawalOperationsPage';
import FinanceDepositOperations from './features/finance/DepositOperationsPage';
import FinancePlatformBalance from './features/finance/PlatformBalancePage';
import HoldingPeriod from './features/finance/HoldingPeriodPage';
import AuditLogs from './features/audit/AuditLogsPage';
import Roles from './features/roles/RolesPage';
import KycList from './features/kyc/KycListPage';
import BankAccounts from './features/kyc/BankAccountsPage';
// Kept for future use — role assignment is currently done inside Edit User on the Users page.
// import AdminRoles from './features/roles/AdminRolesPage';

const App: React.FC = () => {
  return (
    <AuthProvider>
      <Router>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/users"
            element={
              <ProtectedRoute>
                <Users />
              </ProtectedRoute>
            }
          />
          <Route
            path="/audit-logs"
            element={
              <ProtectedRoute>
                <AuditLogs />
              </ProtectedRoute>
            }
          />
          <Route
            path="/user-activities"
            element={
              <ProtectedRoute>
                <UserActivities />
              </ProtectedRoute>
            }
          />
          <Route
            path="/p2p"
            element={
              <ProtectedRoute>
                <P2P mode="p2p" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/p2p/disputes"
            element={
              <ProtectedRoute>
                <Disputes mode="p2p" />
              </ProtectedRoute>
            }
          />
          {/* OTC (cash in person) — same engine as P2P, separate section.
              Скрыт до готовности модуля: см. OTC_ENABLED в shared/constants/features.ts */}
          {OTC_ENABLED && (
            <Route
              path="/otc"
              element={
                <ProtectedRoute>
                  <P2P mode="otc" />
                </ProtectedRoute>
              }
            />
          )}
          {OTC_ENABLED && (
            <Route
              path="/otc/disputes"
              element={
                <ProtectedRoute>
                  <Disputes mode="otc" />
                </ProtectedRoute>
              }
            />
          )}
          {OTC_ENABLED && (
            <Route
              path="/otc/disputes/:id"
              element={
                <ProtectedRoute>
                  <DisputeDetail />
                </ProtectedRoute>
              }
            />
          )}
          {OTC_ENABLED && (
            <Route
              path="/otc/settings"
              element={
                <ProtectedRoute>
                  <OtcSettings />
                </ProtectedRoute>
              }
            />
          )}
          <Route
            path="/p2p/disputes/:id"
            element={
              <ProtectedRoute>
                <DisputeDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/p2p/payment-methods"
            element={
              <ProtectedRoute>
                <PaymentMethods />
              </ProtectedRoute>
            }
          />
          <Route
            path="/p2p/rates"
            element={
              <ProtectedRoute>
                <Rates mode="p2p" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/p2p/restrictions"
            element={
              <ProtectedRoute>
                <P2pRestrictions mode="p2p" />
              </ProtectedRoute>
            }
          />
          {OTC_ENABLED && (
            <Route
              path="/otc/rates"
              element={
                <ProtectedRoute>
                  <Rates mode="otc" />
                </ProtectedRoute>
              }
            />
          )}
          {OTC_ENABLED && (
            <Route
              path="/otc/restrictions"
              element={
                <ProtectedRoute>
                  <P2pRestrictions mode="otc" />
                </ProtectedRoute>
              }
            />
          )}
          <Route
            path="/swap"
            element={
              <ProtectedRoute>
                <Swaps />
              </ProtectedRoute>
            }
          />
          <Route
            path="/swap/rates"
            element={
              <ProtectedRoute>
                <SwapRates />
              </ProtectedRoute>
            }
          />
          <Route
            path="/withdrawal"
            element={
              <ProtectedRoute>
                <Withdrawals />
              </ProtectedRoute>
            }
          />
          <Route
            path="/withdrawal/rates"
            element={
              <ProtectedRoute>
                <WithdrawalRates />
              </ProtectedRoute>
            }
          />
          <Route
            path="/withdrawal/hot-wallets"
            element={
              <ProtectedRoute>
                <HotWallets />
              </ProtectedRoute>
            }
          />
          <Route
            path="/ledger/wallets"
            element={
              <ProtectedRoute>
                <Wallets />
              </ProtectedRoute>
            }
          />
          <Route
            path="/ledger/balances"
            element={
              <ProtectedRoute>
                <Balances />
              </ProtectedRoute>
            }
          />
          <Route
            path="/ledger/deposits"
            element={
              <ProtectedRoute>
                <Deposits />
              </ProtectedRoute>
            }
          />
          <Route
            path="/finance/platform-rate"
            element={
              <ProtectedRoute>
                <FinancePlatformRate />
              </ProtectedRoute>
            }
          />
          <Route
            path="/finance/operations"
            element={
              <ProtectedRoute>
                <FinanceOperations />
              </ProtectedRoute>
            }
          />
          <Route
            path="/finance/operations/p2p"
            element={
              <ProtectedRoute>
                <FinanceP2pOperations />
              </ProtectedRoute>
            }
          />
          <Route
            path="/finance/operations/swap"
            element={
              <ProtectedRoute>
                <FinanceSwapOperations />
              </ProtectedRoute>
            }
          />
          <Route
            path="/finance/operations/withdrawals"
            element={
              <ProtectedRoute>
                <FinanceWithdrawalOperations />
              </ProtectedRoute>
            }
          />
          <Route
            path="/finance/operations/deposits"
            element={
              <ProtectedRoute>
                <FinanceDepositOperations />
              </ProtectedRoute>
            }
          />
          <Route
            path="/finance/platform-balance"
            element={
              <ProtectedRoute>
                <FinancePlatformBalance />
              </ProtectedRoute>
            }
          />
          <Route
            path="/p2p/holding-period"
            element={
              <ProtectedRoute>
                <HoldingPeriod mode="p2p" />
              </ProtectedRoute>
            }
          />
          {OTC_ENABLED && (
            <Route
              path="/otc/holding-period"
              element={
                <ProtectedRoute>
                  <HoldingPeriod mode="otc" />
                </ProtectedRoute>
              }
            />
          )}
          <Route
            path="/roles"
            element={
              <ProtectedRoute>
                <Roles />
              </ProtectedRoute>
            }
          />
          <Route
            path="/kyc"
            element={
              <ProtectedRoute>
                <KycList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/bank-accounts"
            element={
              <ProtectedRoute>
                <BankAccounts />
              </ProtectedRoute>
            }
          />
          {/* Admin Access route hidden — kept commented so we can restore it later if needed. */}
          {/* <Route
            path="/roles/admins"
            element={
              <ProtectedRoute>
                <AdminRoles />
              </ProtectedRoute>
            }
          /> */}
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </Router>
    </AuthProvider>
  );
};

export default App;
